import { NonTimezoneSensitiveDate } from './non-time-zone-sensitive-date';

/**
 * Calculates the fiscal year for a given date based on the fiscal year end configuration.
 *
 * The fiscal year label is based on the START year of the fiscal year period.
 * For example, if FY ends March 31, 2024, the fiscal year started April 1, 2023,
 * so the label is "FY 2023".
 *
 * @param dateString - The date string to calculate the fiscal year for (ISO format or DD-MM-YYYY)
 * @param fyEndMonth - The month (1-12) when the fiscal year ends
 * @param fyEndDay - The day (1-31) when the fiscal year ends
 * @returns The fiscal year number (based on the start year of the fiscal period)
 *
 * @example
 * // FY ends March 31
 * getFiscalYearFromDate('2024-01-15', 3, 31) // returns 2023 (FY started April 1, 2023)
 * getFiscalYearFromDate('2024-04-15', 3, 31) // returns 2024 (FY started April 1, 2024)
 *
 * // FY ends December 31 (calendar year)
 * getFiscalYearFromDate('2024-06-15', 12, 31) // returns 2024
 */
export function getFiscalYearFromDate(
  dateString: string,
  fyEndMonth: number,
  fyEndDay: number
): number {
  // Parse the date string - handle both ISO format and DD-MM-YYYY
  let studyDate: NonTimezoneSensitiveDate;

  if (dateString.includes('-') && dateString.split('-')[0].length === 2) {
    // DD-MM-YYYY format
    const [day, month, year] = dateString.split('-').map(Number);
    studyDate = new NonTimezoneSensitiveDate(year, month, day);
  } else {
    // ISO format or other standard format
    studyDate = NonTimezoneSensitiveDate.fromNativeDate(new Date(dateString));
  }

  const studyYear = studyDate.getYear();

  // Create FY end date for the study date's calendar year
  const fyEndThisYear = new NonTimezoneSensitiveDate(
    studyYear,
    fyEndMonth,
    fyEndDay
  );

  // Determine which fiscal year the study date falls into
  if (studyDate.gt(fyEndThisYear)) {
    // Study date is AFTER this year's FY end
    // So we're in the fiscal year that STARTS this calendar year
    // The FY start date is the day after the FY end
    return studyYear;
  } else {
    // Study date is ON or BEFORE this year's FY end
    // So we're in the fiscal year that STARTED after the previous year's FY end
    const prevFYEnd = new NonTimezoneSensitiveDate(
      studyYear - 1,
      fyEndMonth,
      fyEndDay
    );
    const fyStartDate = prevFYEnd.add(1, 'days');
    return fyStartDate.getYear();
  }
}

/**
 * Default fiscal year end (calendar year - December 31)
 */
export const DEFAULT_FY_END_MONTH = 12;
export const DEFAULT_FY_END_DAY = 31;
